import React, { useCallback, useEffect, useState } from 'react';
import { DeleteOutlined, InfoCircleOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Card, Col, Form, Input, notification, Row, Select, Space, Spin, Tooltip } from 'antd';
import { useIntl } from 'react-intl';
import { ObtPeppolData, ObtUserRestriction } from '../../../__generated__/types';
import {
    useUpdateCompanyPeppolIdMutation,
    useRemoveCompanyPeppolIdMutation,
} from '../obtMutations.generated';

// ─── Constants ─────────────────────────────────────────────────────────────────

const LEGAL_TAB_NAME = 'PEPPOL';

// ─── Types ─────────────────────────────────────────────────────────────────────

type ObtPeppolFormProps = {
    currentPeppolId?: string | null;
    partnerno: string;
    companycode: string;
    idpartner: number;
    country: string;
    peppolData?: ObtPeppolData[] | null;
    canUpdate: boolean;
    userRestrictions: ObtUserRestriction[];
};

// ─── Helpers ───────────────────────────────────────────────────────────────────

function hasLegalRestrictionAccess(restrictions: ObtUserRestriction[], tabName: string): boolean {
    if (!restrictions || restrictions.length === 0) return true;
    return restrictions.some((r) => r.legalTabName === tabName);
}

// ─── Component ─────────────────────────────────────────────────────────────────

const ObtPeppolForm: React.FC<ObtPeppolFormProps> = ({
    currentPeppolId,
    partnerno,
    companycode,
    idpartner,
    country,
    peppolData,
    canUpdate,
    userRestrictions,
}) => {
    const intl = useIntl();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [selectedIcdCode, setSelectedIcdCode] = useState<string | undefined>(undefined);
    const [composedPeppolId, setComposedPeppolId] = useState<string>('');

    const [savePeppol] = useUpdateCompanyPeppolIdMutation();
    const [deletePeppol] = useRemoveCompanyPeppolIdMutation();

    // Initialize form from current PEPPOL ID (format: "icdcode:customerid")
    useEffect(() => {
        if (currentPeppolId) {
            const parts = currentPeppolId.split(':');
            const icd = parts[0] ?? '';
            const custId = parts[1] ?? '';
            form.setFieldsValue({
                peppolId: currentPeppolId,
                customerIdentificationCode: custId,
                icdCode: icd,
            });
            setSelectedIcdCode(icd);
            setComposedPeppolId(currentPeppolId);
        } else {
            form.resetFields();
            setSelectedIcdCode(undefined);
            setComposedPeppolId('');
        }
    }, [partnerno, companycode, country, peppolData, currentPeppolId, idpartner, form]);

    const canAccess = hasLegalRestrictionAccess(userRestrictions, LEGAL_TAB_NAME);
    const isDisabled = !canUpdate || !canAccess;

    const handleIcdChange = useCallback((value: string) => {
        setSelectedIcdCode(value);
        form.setFieldsValue({ peppolId: '', customerIdentificationCode: '' });
        setComposedPeppolId('');
    }, [form]);

    const handleCustomerIdChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const newPeppolId = `${selectedIcdCode}:${value}`;
        setComposedPeppolId(newPeppolId);
        form.setFieldsValue({ peppolId: newPeppolId });
    }, [selectedIcdCode, form]);

    const validateCustomerIdentificationCode = useCallback((_rule: unknown, value: string): Promise<void> => {
        if (!peppolData || !selectedIcdCode) return Promise.resolve();
        const entry = peppolData.find((item) => item.icdcode === selectedIcdCode);
        const expectedLength = entry?.valididlength;
        if (!expectedLength) return Promise.resolve();

        if (!value || value.length !== expectedLength) {
            return Promise.reject(
                intl.formatMessage(
                    { id: 'obt.legal.peppol.customerIdCode.lengthError', defaultMessage: 'Must be exactly {length} characters for country {country}.' },
                    { length: expectedLength, country }
                )
            );
        }
        return Promise.resolve();
    }, [peppolData, selectedIcdCode, country, intl]);

    const handleSave = async () => {
        if (!canAccess) {
            notification.error({
                message: intl.formatMessage({ id: 'obt.legal.restriction.error', defaultMessage: 'You do not have permission for this legal tab.' }),
            });
            return;
        }

        try {
            await form.validateFields();
        } catch {
            return;
        }

        setLoading(true);
        try {
            await savePeppol({
                variables: { peppolId: composedPeppolId, idpartner, partnerno, companycode },
            });
            notification.success({
                message: intl.formatMessage({ id: 'obt.legal.peppol.save.success', defaultMessage: 'PEPPOL ID saved successfully.' }),
            });
        } catch (err) {
            console.error('Failed to save PEPPOL ID:', err);
            notification.error({
                message: intl.formatMessage({ id: 'obt.legal.peppol.save.error', defaultMessage: 'Failed to save PEPPOL ID.' }),
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!canAccess) {
            notification.error({
                message: intl.formatMessage({ id: 'obt.legal.restriction.error', defaultMessage: 'You do not have permission for this legal tab.' }),
            });
            return;
        }

        setLoading(true);
        try {
            await deletePeppol({
                variables: { idpartner, partnerno, companycode },
            });
            form.setFieldsValue({ peppolId: '', customerIdentificationCode: '', icdCode: undefined });
            setSelectedIcdCode(undefined);
            setComposedPeppolId('');
            notification.success({
                message: intl.formatMessage({ id: 'obt.legal.peppol.delete.success', defaultMessage: 'PEPPOL ID deleted successfully.' }),
            });
        } catch (err) {
            console.error('Failed to delete PEPPOL ID:', err);
            notification.error({
                message: intl.formatMessage({ id: 'obt.legal.peppol.delete.error', defaultMessage: 'Failed to delete PEPPOL ID.' }),
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Spin spinning={loading} size="large">
            <Form layout="vertical" onFinish={handleSave} form={form} initialValues={{ country }}>
                <Card
                    title={intl.formatMessage({ id: 'obt.legal.peppol.title', defaultMessage: 'PEPPOL Legal Details' })}
                    size="small"
                    
                >
                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="country"
                                label={intl.formatMessage({ id: 'obt.legal.peppol.country', defaultMessage: 'Country' })}
                            >
                                <Input disabled={true} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="icdCode"
                                label={
                                    <span>
                                        {intl.formatMessage({ id: 'obt.legal.peppol.icdValue', defaultMessage: 'ICD Value' })}{' '}
                                        <Tooltip title={intl.formatMessage({
                                            id: 'obt.legal.peppol.note.description',
                                            defaultMessage: 'The Customer Identification code is the code which identifies the customer and can be either a VAT, Bank or Enterprise number, depending on which ICD Value is selected.',
                                        })}>
                                            <InfoCircleOutlined style={{ color: 'rgba(0,0,0,0.45)', cursor: 'pointer' }} />
                                        </Tooltip>
                                    </span>
                                }
                                rules={[{ required: true }]}
                            >
                                <Select
                                    showSearch={true}
                                    placeholder={intl.formatMessage({ id: 'obt.legal.peppol.icdValue.placeholder', defaultMessage: 'Select ICD Value' })}
                                    optionFilterProp="children"
                                    onChange={handleIcdChange}
                                    disabled={isDisabled}
                                >
                                    {peppolData?.map((item, index) => (
                                        <Select.Option
                                            key={`${item.icdcode}_${index}`}
                                            value={item.icdcode ?? ''}
                                        >
                                            {`${item.icdcode} - ${item.schemename}`}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="customerIdentificationCode"
                                label={intl.formatMessage({ id: 'obt.legal.peppol.customerIdCode', defaultMessage: 'Customer Identification Code' })}
                                rules={[
                                    { required: true, message: intl.formatMessage({ id: 'obt.legal.peppol.customerIdCode.required', defaultMessage: 'Required' }) },
                                    { validator: validateCustomerIdentificationCode },
                                ]}
                            >
                                <Input
                                    placeholder={intl.formatMessage({ id: 'obt.legal.peppol.customerIdCode', defaultMessage: 'Customer Identification Code' })}
                                    onChange={handleCustomerIdChange}
                                    disabled={isDisabled}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="peppolId"
                                label={intl.formatMessage({ id: 'obt.legal.peppol.peppolId', defaultMessage: 'PEPPOL ID' })}
                            >
                                <Input readOnly={true} disabled={true} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col>
                            <Space>
                                <Button
                                    icon={<SaveOutlined />}
                                    type="primary"
                                    htmlType="submit"
                                    disabled={isDisabled}
                                >
                                    {intl.formatMessage({ id: 'obt.legal.btn.save', defaultMessage: 'Save' })}
                                </Button>
                                <Button
                                    icon={<DeleteOutlined />}
                                    onClick={handleDelete}
                                    disabled={isDisabled}
                                >
                                    {intl.formatMessage({ id: 'obt.legal.btn.delete', defaultMessage: 'Delete' })}
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                </Card>
            </Form>
        </Spin>
    );
};

export default ObtPeppolForm;

